import { useCallback } from 'react';

import { IntakeFormReviewSource, SchedulePreviewAppointment } from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog/logger';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { FORM_LIST_INTAKE_FORM_TITLE } from '../measures/constants';
import { ProviderDrawerState } from '../state';
import { useLazyGetFormsByPatientShortId } from '../useGetFormsByPatientShortId';
import { formDataByPatientShortIdAtom } from './state';
import { useOpenClientFormsCallback } from './useOpenClientFormsCallback';
import { useOpenClientInfoDrawerForAppointment } from './useOpenClientInfoDrawerForAppointment';

type OpenIntakeFormResponseArgs = (
  | {
      appointment: SchedulePreviewAppointment['node'];
      patientShortId?: never;
    }
  | {
      appointment?: never;
      patientShortId: string;
    }
) & {
  /** Required so a new entry point can't silently report the review as `unknown`. */
  source: IntakeFormReviewSource;
};

/**
 * Opens the intake form response in the client information drawer.
 * When an appointment is provided (e.g. agenda Prep card), sets appointment
 * context and opens directly to the form response (with a loading state while
 * forms load) rather than flashing the overview tab first.
 */
export function useOpenIntakeFormResponseCallback() {
  const openClientInfoDrawerForAppointment = useOpenClientInfoDrawerForAppointment();
  const openClientForms = useOpenClientFormsCallback();
  const [fetchForms] = useLazyGetFormsByPatientShortId();
  const getFormDataByPatient = useAtomCallbackMemoized((get) => get(formDataByPatientShortIdAtom), []);

  return useCallback(
    async ({ appointment, patientShortId, source }: OpenIntakeFormResponseArgs) => {
      const resolvedPatientShortId = appointment?.patient?.shortId ?? patientShortId;
      if (!resolvedPatientShortId) {
        logger.error('Missing patient short ID when opening intake form response');
        return;
      }

      // Open form response immediately so the user never lands on overview while
      // waiting for forms. The form response drawer shows its own loading state
      // until form data is available (often already prefetched by IntakeSummary).
      if (appointment) {
        openClientInfoDrawerForAppointment(appointment, ProviderDrawerState.CLIENT_FORM_RESPONSE);
      }
      openClientForms(FORM_LIST_INTAKE_FORM_TITLE, source);

      const formDataByPatient = getFormDataByPatient();
      if (!formDataByPatient[resolvedPatientShortId]) {
        const { error } = await fetchForms({
          variables: { patientShortId: resolvedPatientShortId },
        });
        if (error) {
          logger.error(
            'Unable to fetch patient forms before opening intake form response',
            { patientShortId: resolvedPatientShortId },
            error,
          );
        }
      }
    },
    [fetchForms, getFormDataByPatient, openClientForms, openClientInfoDrawerForAppointment],
  );
}
